import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import CaseCard from "@/components/feature/CaseCard";
import TipOffSection from "@/components/feature/TipOffSection";
import ArchiveFilters from "./components/ArchiveFilters";
import { useSeo } from "@/hooks/useSeo";
import { useHashScroll } from "@/hooks/useHashScroll";
import { cases, categories } from "@/mocks/cases";
import { formatHeat } from "@/utils/format";

const ARCHIVE_TITLE = "爆料档案库｜独家黑料实锤截图与录音证据链索引";
const ARCHIVE_DESC =
  "按行程时间线、商业内幕、合约风波、圈内口碑分类浏览全部独家黑料档案，每条均标注独家黑料截图、录音与细节的出处，支持关键词搜索快速定位事件。";
const ARCHIVE_KEYWORDS = "独家黑料出处,独家黑料细节,独家黑料实锤,爆料档案库,证据链索引";

const archiveJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      name: ARCHIVE_TITLE,
      description: ARCHIVE_DESC,
      inLanguage: "zh-CN",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://yijiangshui2023.cn/" },
        { "@type": "ListItem", position: 2, name: "爆料档案库" },
      ],
    },
  ],
};

export default function Archive() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") ?? "all";
  const query = searchParams.get("q") ?? "";

  useSeo({
    title: ARCHIVE_TITLE,
    description: ARCHIVE_DESC,
    keywords: ARCHIVE_KEYWORDS,
    path: "/archive",
    jsonLd: archiveJsonLd,
  });
  useHashScroll();

  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    cases.forEach((item) => {
      map[item.category] = (map[item.category] ?? 0) + 1;
    });
    return map;
  }, []);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return [...cases]
      .filter((item) => (activeCat === "all" ? true : item.category === activeCat))
      .filter((item) => {
        if (!keyword) return true;
        const haystack = [item.title, item.summary, item.categoryName, ...item.tags, ...item.evidences]
          .join(" ")
          .toLowerCase();
        return haystack.includes(keyword);
      })
      .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
  }, [activeCat, query]);

  const updateParams = (next: { cat?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.cat !== undefined) {
      if (next.cat === "all") params.delete("cat");
      else params.set("cat", next.cat);
    }
    if (next.q !== undefined) {
      if (!next.q.trim()) params.delete("q");
      else params.set("q", next.q);
    }
    setSearchParams(params, { replace: true });
  };

  const activeCategory = categories.find((c) => c.id === activeCat) ?? categories[0];
  const totalEvidence = filtered.reduce((sum, item) => sum + item.evidenceList.length, 0);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="px-4 md:px-8 py-8 md:py-12">
            <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "爆料档案库" }]} />
            <h1 className="mt-5 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              爆料档案库：独家黑料实锤证据链索引
            </h1>
            <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-600">
              这里收录站内全部整理完毕的档案。分类只是入口，材料才是重点——每条都标注了证据类型、出处与当前补充状态。
            </p>
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-sm text-foreground-600">
              <span className="flex items-center gap-2">
                <i className="ri-folder-3-line text-primary-500" />
                共 {cases.length} 条档案
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-links-line text-secondary-600" />
                证据条目 {totalEvidence} 项
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-pulse-line text-accent-600" />
                热度最高 {formatHeat(Math.max(...cases.map((c) => c.heat)))}
              </span>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <ArchiveFilters
            activeCat={activeCat}
            query={query}
            counts={counts}
            total={cases.length}
            onCatChange={(id) => updateParams({ cat: id })}
            onQueryChange={(value) => updateParams({ q: value })}
          />

          <p className="mt-6 text-sm text-foreground-500">
            当前筛选：<span className="text-foreground-800 font-medium">{activeCategory.name}</span>
            {query.trim() && <span> · 关键词「{query.trim()}」</span>}
            <span> · 命中 {filtered.length} 条</span>
          </p>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filtered.map((item) => (
                <CaseCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 py-16 px-6 text-center">
              <span className="mx-auto flex w-14 h-14 items-center justify-center rounded-md bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-2xl" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有匹配的档案</h2>
              <p className="mt-2 text-sm text-foreground-600">换个关键词，或者回到全部分类看看。</p>
              <button
                type="button"
                onClick={() => updateParams({ cat: "all", q: "" })}
                className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          )}

          <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-sm leading-relaxed text-foreground-700">
              没找到你在意的事件？把线索投递给我们，核对后会建档并标注出处。
            </p>
            <Link
              to="/archive#tips"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-megaphone-line" />
              投递线索
            </Link>
          </div>
        </section>

        <TipOffSection />
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}